import { DateTime } from 'luxon';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';

const STATUS_STYLES: Record<string, { label: string; backgroundColor: string }> = {
  unpaid: { label: 'Unpaid', backgroundColor: '#E5E7EB' },
  in_progress: { label: 'In progress', backgroundColor: '#DBEAFE' },
  paid: { label: 'Paid', backgroundColor: '#D1FAE5' },
  overdue: { label: 'Overdue', backgroundColor: '#FEE2E2' },
  void: { label: 'Void', backgroundColor: '#F3F4F6' }
};

const APPROVAL_STYLES: Record<
  'pending' | 'approved' | 'declined',
  { backgroundColor: string; baseLabel: string }
> = {
  pending: { backgroundColor: '#E5E7EB', baseLabel: 'Pending' },
  approved: { backgroundColor: '#D1FAE5', baseLabel: 'Approved' },
  declined: { backgroundColor: '#FEE2E2', baseLabel: 'Declined' }
};

/**
 * Build the chip label for an approval column. Adds an "by <name>"
 * byline when the approver is joined into the row (getBills does the
 * LEFT JOIN to Users). No name → bare status label.
 */
const buildApprovalChip = (
  status: 'pending' | 'approved' | 'declined' | null | undefined,
  approvedByName: string | null | undefined
): TableCellChip => {
  const key = status && APPROVAL_STYLES[status] ? status : 'pending';
  const meta = APPROVAL_STYLES[key];
  const label =
    key === 'pending' || !approvedByName ? meta.baseLabel : `${meta.baseLabel} · ${approvedByName}`;
  return {
    type: 'chip',
    data: [{ text: label, backgroundColor: meta.backgroundColor, size: 'small' }]
  };
};

const NOTES_PREVIEW_LEN = 40;

const gbpFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
};

/**
 * Sum subscription + overage for the row's total cost cell. Returns "—"
 * only when *both* sides are unknown (historical bills that predate the
 * cost columns) so operators can tell "unknown" from "confirmed £0.00".
 * A missing side is treated as 0 in the sum when the other side is known.
 */
const formatTotalCost = (
  subscription: number | string | null | undefined,
  overage: number | string | null | undefined
): string => {
  const sub = toNumber(subscription);
  const over = toNumber(overage);
  if (sub === null && over === null) return '—';
  return gbpFormatter.format((sub ?? 0) + (over ?? 0));
};

/**
 * Build the public share URL for an artifact fileName using the same
 * `/a/:fileName` route that the artifacts page uses. Returns null if
 * the fileName is missing — in that case the caller opens a
 * confirmation dialog to generate the artifact on demand.
 */
const artifactShareUrl = (fileName: string | null | undefined): string | null => {
  if (!fileName) return null;
  return `${process.env.REACT_APP_API_BASE}/a/${fileName}`;
};

const generateRows = (
  tokens: Bill[],
  onEditBill: (token: Bill) => void,
  onOpenArtifact: (token: Bill, kind: 'invoice' | 'report') => void,
  onDeleteBill: (token: Bill) => void,
  onManageApprovals: (token: Bill) => void
): TableRow[] => {
  // Trust the server-side order. The previous client-side sort grouped by
  // `billingAccountId | startDate`, which is effectively a UUID sort and
  // scrambled bills out of date order in the UI.
  return tokens.map((token) => {
    const availableToken: TableCellText = {
      data: {
        text: token.availableToken,
        fontColor: Number(token.availableToken) < 0 ? '#D32F33' : 'rgba(0, 0, 0, 0.87)'
      },
      type: 'text'
    };

    const reportShareUrl = artifactShareUrl(token.reportUrl);
    const invoiceShareUrl = artifactShareUrl(token.invoiceUrl);

    const reportAction: TableCellAction = {
      type: 'action',
      // Collapse the 5 per-row actions into a three-dot menu so the
      // Actions column doesn't dominate the horizontal width. Each
      // item's `tooltip` doubles as its menu label.
      renderAs: 'menu',
      data: [
        {
          type: 'other',
          icon: AssessmentIcon,
          tooltip: reportShareUrl ? 'Open report' : 'Generate report',
          linkData: {
            onClick: () => onOpenArtifact(token, 'report')
          }
        },
        {
          type: 'other',
          icon: ReceiptIcon,
          tooltip: invoiceShareUrl ? 'Open invoice' : 'Generate invoice',
          linkData: {
            onClick: () => onOpenArtifact(token, 'invoice')
          }
        },
        {
          type: 'other',
          icon: CheckCircleIcon,
          tooltip: 'Manage approvals',
          linkData: {
            onClick: () => onManageApprovals(token)
          }
        },
        {
          type: 'edit',
          icon: EditIcon,
          tooltip: 'Edit bill',
          linkData: {
            onClick: () => {
              onEditBill(token);
            }
          }
        },
        {
          type: 'other',
          icon: DeleteIcon,
          tooltip: 'Delete bill',
          linkData: {
            onClick: () => onDeleteBill(token)
          }
        }
      ]
    };

    const statusMeta = STATUS_STYLES[token.status ?? 'unpaid'] ?? STATUS_STYLES.unpaid;
    const statusCell: TableCellChip = {
      type: 'chip',
      data: [{ text: statusMeta.label, backgroundColor: statusMeta.backgroundColor, size: 'small' }]
    };

    const notesText = token.notes ?? '';
    const notesPreview =
      notesText.length > NOTES_PREVIEW_LEN ? `${notesText.slice(0, NOTES_PREVIEW_LEN)}…` : notesText;

    return {
      companyName: token.companyName ?? '',
      allocatedToken: token.allocatedToken ? token.allocatedToken : 0,
      availableToken,
      consumeToken: token.consumeToken ? token.consumeToken : 0,
      leadCount: token.leadCount ?? '—',
      totalCost: formatTotalCost(token.subscriptionCost, token.overageCost),
      billing: `${token.startDate ? DateTime.fromISO(token.startDate).toFormat('dd LLL') : ''} - ${
        token.endDate ? DateTime.fromISO(token.endDate).toFormat('dd LLL, yyyy') : ''
      }`,
      // Display the generation date (period end + 1 day). A bill covering
      // Jun 12 → Jul 11 is written on Jul 12 by the daily cron, so that
      // is the date the admin expects to see in the Date column.
      endDate: token.endDate
        ? DateTime.fromISO(token.endDate).plus({ days: 1 }).toFormat('dd LLL, yyyy')
        : 0,
      status: statusCell,
      invoiceApproval: buildApprovalChip(token.invoiceApprovalStatus, token.invoiceApprovedByName),
      reportApproval: buildApprovalChip(token.reportApprovalStatus, token.reportApprovedByName),
      notes: notesPreview,
      reportAction
    };
  });
};

export default generateRows;
